import { useState, useRef } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, Upload, X, Loader2, ChevronLeft } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { markNotified } from "@/lib/notified-sold";

type Categoria = "vendedor" | "anuncio";
type SubcategoriaAnuncio = "item_vendido" | "outro";
type Step = "select_category" | "select_sub" | "form";

interface ReportProblemDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anuncioId: string;
  itemNome: string;
  tier?: number | null;
  servidor?: string | null;
  onReported?: () => void;
}

const getNotificantePhone = (): string | null => {
  try {
    const raw = localStorage.getItem("rpgcoin_client_session");
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.canal === "whatsapp" && parsed?.contato) return parsed.contato;
    return null;
  } catch {
    return null;
  }
};

const ReportProblemDialog = ({ open, onOpenChange, anuncioId, itemNome, tier, servidor, onReported }: ReportProblemDialogProps) => {
  const [step, setStep] = useState<Step>("select_category");
  const [categoria, setCategoria] = useState<Categoria | null>(null);
  const [subcategoria, setSubcategoria] = useState<SubcategoriaAnuncio | null>(null);
  const [descricao, setDescricao] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setStep("select_category");
    setCategoria(null);
    setSubcategoria(null);
    setDescricao("");
    setFiles([]);
  };

  const handleOpenChange = (o: boolean) => {
    if (!o) reset();
    onOpenChange(o);
  };

  const handleSelectCategoria = (cat: Categoria) => {
    setCategoria(cat);
    if (cat === "vendedor") {
      setSubcategoria(null);
      setStep("form");
    } else {
      setStep("select_sub");
    }
  };

  const handleSelectSub = (sub: SubcategoriaAnuncio) => {
    setSubcategoria(sub);
    setStep("form");
  };

  const handleAddFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFiles(prev => [...prev, ...Array.from(e.target.files!)].slice(0, 10));
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemoveFile = (idx: number) => {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  };

  const needsDescription = categoria === "vendedor" || subcategoria === "outro";
  const isDescriptionValid = !needsDescription || descricao.trim().length >= 5;
  const canSubmit = files.length >= 1 && isDescriptionValid && !loading;

  const getTitle = () => {
    if (categoria === "vendedor") return "Problema com o Vendedor";
    if (subcategoria === "item_vendido") return "Item Já Foi Vendido";
    if (subcategoria === "outro") return "Outro Problema com o Anúncio";
    return "Reportar Problema";
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setLoading(true);
    try {
      // Upload files
      const urls: string[] = [];
      for (const file of files) {
        const ext = file.name.split(".").pop() || "bin";
        const path = `${anuncioId}/${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
        const { error: upErr } = await supabase.storage.from("report-anexos").upload(path, file);
        if (upErr) {
          console.error("Upload error:", upErr);
          continue;
        }
        const { data: urlData } = supabase.storage.from("report-anexos").getPublicUrl(path);
        urls.push(urlData.publicUrl);
      }

      if (urls.length === 0) {
        toast({ title: "Erro no upload dos arquivos", variant: "destructive" });
        setLoading(false);
        return;
      }

      const catValue = categoria === "vendedor" ? "vendedor" : (subcategoria || "item_vendido");
      const notificante_telefone = getNotificantePhone();

      // Get session credentials for auth
      let contato = "";
      let token = "";
      try {
        const raw = localStorage.getItem("rpgcoin_client_session");
        if (raw) {
          const parsed = JSON.parse(raw);
          contato = parsed?.contato || "";
          token = parsed?.token || "";
        }
      } catch {}

      if (!contato || !token) {
        toast({ title: "Você precisa estar logado para reportar um problema.", variant: "destructive" });
        setLoading(false);
        return;
      }

      const { error } = await supabase.functions.invoke("notify-item-sold", {
        body: {
          anuncio_id: anuncioId,
          item_nome: itemNome,
          tier: tier ?? null,
          servidor: servidor ?? null,
          descricao: descricao.trim() || null,
          categoria: catValue,
          subcategoria: subcategoria || null,
          anexos_urls: urls,
          notificante_telefone,
          contato,
          token,
        },
      });

      if (error) throw error;

      markNotified(anuncioId);
      toast({ title: "Problema reportado com sucesso!" });
      onReported?.();
      handleOpenChange(false);
    } catch (err: any) {
      toast({ title: err.message || "Erro ao reportar", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="tibia-border bg-card max-w-sm" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle className="font-pixel text-xs text-primary flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            {getTitle()}
          </DialogTitle>
          <DialogDescription className="text-xs text-muted-foreground">
            {step === "select_category" && "Selecione o tipo de problema:"}
            {step === "select_sub" && "Qual o problema com o anúncio?"}
            {step === "form" && "Preencha os detalhes e anexe ao menos um arquivo."}
          </DialogDescription>
        </DialogHeader>

        {/* Step 1: Category selection */}
        {step === "select_category" && (
          <div className="space-y-2">
            <button
              onClick={() => handleSelectCategoria("vendedor")}
              className="tibia-border-inner bg-secondary/30 rounded-sm p-3 w-full text-left hover:bg-secondary/50 transition-colors"
            >
              <span className="font-pixel text-[9px] text-foreground">👤 Problema com o Vendedor</span>
              <p className="text-[10px] text-muted-foreground mt-0.5">Reportar comportamento inadequado do vendedor</p>
            </button>
            <button
              onClick={() => handleSelectCategoria("anuncio")}
              className="tibia-border-inner bg-secondary/30 rounded-sm p-3 w-full text-left hover:bg-secondary/50 transition-colors"
            >
              <span className="font-pixel text-[9px] text-foreground">📋 Problema com o Anúncio</span>
              <p className="text-[10px] text-muted-foreground mt-0.5">O item já foi vendido ou há outro problema</p>
            </button>
          </div>
        )}

        {/* Step 2: Sub-category for "anuncio" */}
        {step === "select_sub" && (
          <div className="space-y-2">
            <button
              onClick={() => setStep("select_category")}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors mb-1"
            >
              <ChevronLeft className="w-3 h-3" /> Voltar
            </button>
            <button
              onClick={() => handleSelectSub("item_vendido")}
              className="tibia-border-inner bg-secondary/30 rounded-sm p-3 w-full text-left hover:bg-secondary/50 transition-colors"
            >
              <span className="font-pixel text-[9px] text-foreground">🏷️ O item já foi vendido</span>
            </button>
            <button
              onClick={() => handleSelectSub("outro")}
              className="tibia-border-inner bg-secondary/30 rounded-sm p-3 w-full text-left hover:bg-secondary/50 transition-colors"
            >
              <span className="font-pixel text-[9px] text-foreground">❓ Outro problema</span>
            </button>
          </div>
        )}

        {/* Step 3: Form */}
        {step === "form" && (
          <div className="space-y-3">
            <button
              onClick={() => {
                if (categoria === "vendedor") setStep("select_category");
                else setStep("select_sub");
              }}
              className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronLeft className="w-3 h-3" /> Voltar
            </button>

            {needsDescription && (
              <textarea
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder={
                  categoria === "vendedor"
                    ? "Descreva o problema com o vendedor... (mín. 5 caracteres)"
                    : "Descreva o problema encontrado... (mín. 5 caracteres)"
                }
                maxLength={1000}
                rows={4}
                className="tibia-input rounded-sm w-full text-sm resize-none"
              />
            )}

            {/* File upload */}
            <div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="tibia-tab rounded-sm px-3 py-2 font-pixel text-[8px] flex items-center gap-1.5 w-full justify-center hover:text-primary transition-colors"
              >
                <Upload className="w-3.5 h-3.5" />
                ANEXAR ARQUIVOS ({files.length}/10) *
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,.pdf,.txt,.doc,.docx"
                onChange={handleAddFiles}
                className="hidden"
              />
              <p className="text-[8px] text-muted-foreground mt-1">* Obrigatório ao menos 1 arquivo</p>
            </div>

            {files.length > 0 && (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 bg-secondary/30 rounded-sm px-2 py-1">
                    {f.type.startsWith("image/") && (
                      <img src={URL.createObjectURL(f)} alt="" className="w-8 h-8 object-cover rounded-sm border border-border shrink-0" />
                    )}
                    <span className="text-[10px] text-foreground/80 truncate flex-1">{f.name}</span>
                    <span className="text-[9px] text-muted-foreground whitespace-nowrap">
                      {(f.size / 1024).toFixed(0)} KB
                    </span>
                    <button onClick={() => handleRemoveFile(i)} className="text-destructive hover:text-destructive/80">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-1">
              <button
                onClick={() => handleOpenChange(false)}
                className="tibia-tab rounded-sm px-4 py-1.5 font-pixel text-[8px]"
              >
                Cancelar
              </button>
              <button
                onClick={handleSubmit}
                disabled={!canSubmit}
                className="tibia-btn rounded-sm px-4 py-1.5 font-pixel text-[8px] flex items-center gap-1 disabled:opacity-50"
              >
                {loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Enviando...</> : "Enviar"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ReportProblemDialog;
